import { lazy, Suspense, useMemo, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  List,
  ListItemButton,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import type { HotelPublicContent } from '@bandido/shared/types/hotels/hotel-content';
import type { HotelListOptions, HotelResultCard } from '@bandido/shared/types/hotels/hotel-search-results';
import { type HotelSummary, formatHotelAmount } from './searchModel';
import { HotelContentImage } from './HotelPublishedContent';
import { LocationMapBoundary } from './HotelLocation';
import { googleMapsLocationUrl } from './hotelLocationPoint';
import { hotelMapPoints } from './hotelMapPoints';

const HotelResultsMapCanvas = lazy(() => import('./HotelResultsMapFrame'));

export function HotelResultsMap({
  cards,
  hotels,
  content,
  options,
  resolvedCount,
  contentLoading,
  total,
  busy,
  complete,
  progress,
  onOpen,
  onReload,
  onList,
}: {
  cards: HotelResultCard[];
  hotels: HotelSummary[];
  content: Record<string, HotelPublicContent>;
  options: HotelListOptions;
  resolvedCount: number;
  contentLoading: boolean;
  total?: number;
  busy: boolean;
  complete: boolean;
  progress: { loaded: number; total: number } | null;
  onOpen: (id: string) => void;
  onReload: () => void;
  onList: () => void;
}) {
  const { t } = useTranslation();
  const points = useMemo(() => hotelMapPoints(cards, hotels, content, options), [cards, hotels, content, options]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [group, setGroup] = useState<string[]>([]);
  const selected = points.find((point) => point.id === selectedId) ?? points[0] ?? null;
  const grouped = points.filter((point) => group.includes(point.id));
  const pending = cards.length - resolvedCount;
  const missing = Math.max(0, resolvedCount - points.length);
  const priceSort = options.sort === 'price_asc' || options.sort === 'price_desc';
  const priceLabel = selected?.price
    ? t('hotelSearch.sortPriceFrom', {
      amount: formatHotelAmount(selected.price.price.amount),
      currency: selected.price.price.currency,
      basis: t(`hotelSearch.basis.${selected.price.price_basis}`),
    })
    : t('hotelSearch.mapConsultRates');
  return (
    <Stack gap={2}>
      <Typography role="status">
        {t('hotelSearch.mapCoverage', { located: points.length, total: total ?? cards.length })}
      </Typography>
      {busy && progress && <Typography role="status">{t('hotelSearch.mapResultsProgress', progress)}</Typography>}
      {pending > 0 && (
        <Typography role="status">
          {t(contentLoading ? 'hotelSearch.mapContentProgress' : 'hotelSearch.mapPendingLocations', {
            loaded: resolvedCount,
            total: cards.length,
            count: pending,
          })}
        </Typography>
      )}
      {!complete && !busy && (
        <Alert severity="info">
          {t('hotelSearch.mapIncomplete')}
          <Button onClick={onReload}>{t('hotelSearch.reloadResults')}</Button>
        </Alert>
      )}
      {missing > 0 && (
        <Alert severity="info">
          {t('hotelSearch.mapMissingLocations', { count: missing })}
          <Button onClick={onList}>{t('hotelSearch.listView')}</Button>
        </Alert>
      )}
      <Typography variant="caption">
        {t(priceSort ? 'hotelSearch.wholeStay' : 'hotelSearch.mapPriceHint')}
        {' · '}
        {t('hotelSearch.mapNavigationHint')}
      </Typography>
      {points.length > 0 ? (
        <Box
          sx={{ display: 'grid', gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: 'minmax(0, 1fr) 320px' }, gap: 2 }}
        >
          <LocationMapBoundary fallback={<Alert severity="warning">{t('hotelSearch.resultsMapUnavailable')}</Alert>}>
            <Suspense fallback={<Typography role="status">{t('hotelSearch.mapLoading')}</Typography>}>
              <HotelResultsMapCanvas
                points={points}
                selectedId={selected?.id ?? null}
                onSelect={(ids) => {
                  setGroup(ids.length > 1 ? ids : []);
                  setSelectedId(ids[0] ?? null);
                }}
              />
            </Suspense>
          </LocationMapBoundary>
          <Stack gap={2} sx={{ minWidth: 0 }}>
            <Autocomplete
              options={points}
              value={selected}
              isOptionEqualToValue={(a, b) => a.id === b.id}
              getOptionLabel={(point) => point.name}
              onChange={(_, point) => {
                setSelectedId(point?.id ?? null);
                setGroup([]);
              }}
              renderInput={(params) => <TextField {...params} label={t('hotelSearch.mapSelectHotel')} />}
            />
            {grouped.length > 1 && (
              <Box>
                <Typography variant="subtitle2">{t('hotelSearch.mapCluster', { count: grouped.length })}</Typography>
                <List aria-label={t('hotelSearch.mapGroupHotels')} sx={{ maxHeight: 180, overflowY: 'auto' }}>
                  {grouped.map((point) => (
                    <ListItemButton
                      key={point.id}
                      selected={point.id === selected?.id}
                      onClick={() => setSelectedId(point.id)}
                    >
                      {point.name}
                    </ListItemButton>
                  ))}
                </List>
              </Box>
            )}
            {selected && (
              <Card variant="outlined">
                <CardContent>
                  <Stack gap={1}>
                    <HotelContentImage url={selected.content.main_image?.url} name={selected.name} banner />
                    <Typography variant="h6">{selected.name}</Typography>
                    <Typography variant="body2">
                      {[selected.content.location?.address, selected.content.location?.city]
                        .filter(Boolean)
                        .join(' · ')}
                    </Typography>
                    <Typography>{priceLabel}</Typography>
                    <Button variant="contained" onClick={() => onOpen(selected.id)} sx={{ minHeight: 44 }}>
                      {t('hotelSearch.viewRooms')}
                    </Button>
                    <Button
                      component="a"
                      href={googleMapsLocationUrl(selected)}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label={t('hotelSearch.openExternalMapLabel')}
                      sx={{ minHeight: 44 }}
                    >
                      {t('hotelSearch.openExternalMap')}
                    </Button>
                  </Stack>
                </CardContent>
              </Card>
            )}
          </Stack>
        </Box>
      ) : (
        !busy && pending === 0 && <Typography>{t('hotelSearch.mapNoLocations')}</Typography>
      )}
    </Stack>
  );
}
